import { useState, useContext } from 'react'
import * as Sentry from '@sentry/browser'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { QUESTIONS, ERRORS } from 'src/constants/messages'
import { DirectMessage } from 'src/constants/trackingEvents'
import { selectRouteKey } from 'src/store/selectors/routes'
import { selectClientId, selectClientIsGuest } from 'src/store/selectors/client'
import { selectCustomerProfile } from 'src/store/selectors/customerProfile'
import { selectHomeId } from 'src/store/selectors/home'
import { selectBuyerId } from 'src/store/selectors/buyerInfo'
import { toggleAuthModal } from 'src/actions/homeSearchAuthModal'
import { dispatchEvent } from 'src/helpers/tagManager'
import { ListingDetailsListing } from 'src/types/listings'

import QuestionSingle from './QuestionSingle'
import { flashNotification } from 'src/actions'
import {
  createHomeownerMessage,
  createBuyerMessage,
} from 'src/api/mikasa/requests'
import { AmplitudeContext } from '@homebot/shared-util-quartermaster'

// See apps/clients-frontend/src/store/selectors/listings.js
type ListingMetadata = {
  customer_id: string
  email: string
  listing: ListingDetailsListing
}

interface MessageProps {
  topic: string
  topicKey: string
  message?: string
  clientId?: string
  metadata?: ListingMetadata
}

interface QuestionSingleContainerProps {
  avatarUri?: string
  className?: string
  metadata?: ListingMetadata
  name?: string
  onBlur?: (e) => void
  onFocus?: (e) => void
  onSubmit?: (status, callback) => void
  placeholder?: string
  question?: string
  questionTopic: string
  questionTopicKey: string
}

const QuestionSingleContainer = ({
  className,
  name,
  question,
  questionTopic,
  questionTopicKey,
  metadata,
  placeholder,
  avatarUri,
  onBlur,
  onFocus,
  onSubmit,
}: QuestionSingleContainerProps) => {
  const dispatch = useDispatch()
  const isGuest = useSelector(selectClientIsGuest)
  const isBuyer =
    selectRouteKey() === 'buy' || selectRouteKey() === 'homeSearch'
  const buyerId = useSelector(selectBuyerId)
  const homeId = useSelector(selectHomeId)
  const clientId = useSelector(selectClientId)
  const customerProfile = useSelector(selectCustomerProfile)
  const [inputValue, setInputValue] = useState(question || '')
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const intl = useIntl()
  const { ampli } = useContext(AmplitudeContext)

  // Mark: - Helpers
  const clearInput = () => {
    setInputValue('')
  }

  const toggleSignUpForm = show => {
    dispatch(toggleAuthModal(show))
  }

  const isQuestionValid = () => {
    if (!customerProfile) {
      showFlashMessage({
        message: [
          intl.formatMessage(ERRORS.defaultHeadline),
          intl.formatMessage(ERRORS.defaultMessage),
        ],
        messageType: 'error',
      })

      Sentry.captureMessage(
        'Unable to send dm because customerProfile is not present'
      )
      return false
    }

    return true
  }

  const showFlashMessage = messageParams => {
    dispatch(flashNotification(messageParams))
  }

  const reportMessageCreationError = err => {
    if (err.response != null) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: err.message,
          requestData: err.config.data,
          requestErrors: Object.values(err.response.data.errors),
        },
      })
    } else {
      Sentry.captureException('DM failed to send', {
        extra: {
          error: err.message,
        },
      })
    }
  }
  // End: - Helpers

  // Mark: - Event handlers
  const handleChange = e => {
    setInputValue(e.target.value)
  }

  const handleFocus = e => {
    setIsFocused(true)
    onFocus && onFocus(e)
  }

  const handleBlur = e => {
    setIsFocused(false)
    onBlur && onBlur(e)
  }

  const handleTextAreaClick = e => {
    if (isGuest) {
      toggleSignUpForm(true)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    const valid = isQuestionValid()

    if (!valid) {
      return
    }

    if (isGuest) {
      toggleSignUpForm(true)
      return
    }

    let messageParams: MessageProps = {
      topic: questionTopic,
      topicKey: questionTopicKey,
      message: inputValue || placeholder,
    }

    setIsLoading(true)

    let recordId
    let createMessage

    if (isBuyer) {
      recordId = buyerId
      createMessage = createBuyerMessage
      messageParams = {
        ...messageParams,
        metadata,
      }
    } else {
      recordId = homeId
      createMessage = createHomeownerMessage
      messageParams = {
        ...messageParams,
        clientId,
      }
    }

    let responseMessage
    let isSuccess
    try {
      await createMessage(recordId, messageParams, ampli)
      dispatchEvent(DirectMessage, { eventLabel: questionTopic })
      responseMessage = intl.formatMessage(QUESTIONS.sendSuccess)
      isSuccess = true
    } catch (err) {
      reportMessageCreationError(err)
      responseMessage = intl.formatMessage(QUESTIONS.sendFailure)
      isSuccess = false
    } finally {
      setTimeout(() => {
        setIsLoading(false)
        showFlashMessage({
          message: responseMessage,
          // Just use the pink error banner since success is a gnarly green
          messageType: 'error',
        })
        if (onSubmit) onSubmit(isSuccess, clearInput)
      }, 1000)
    }
  }
  // End: - Event Handlers

  return (
    <QuestionSingle
      avatarUri={avatarUri}
      className={className}
      inputValue={inputValue}
      isFocused={isFocused}
      isLoading={isLoading}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      onFocus={handleFocus}
      onSubmit={handleSubmit}
      onTextAreaClick={handleTextAreaClick}
      placeholder={placeholder}
    />
  )
}

export default QuestionSingleContainer
